export default function Testimonials() {
  const partnerships = [
    {
      sector: 'Aerospace & Defense',
      icon: 'ri-rocket-2-line',
      description:
        'Strategic partnerships with aerospace manufacturers and defense contractors requiring precision cryogenic fueling systems and specialty gas mixtures for advanced propulsion and testing operations.',
      gradient: 'from-cyan-500 to-blue-500',
    },
    {
      sector: 'Energy & Maritime',
      icon: 'ri-ship-2-line',
      description:
        'Integrated port energy systems and maritime fueling infrastructure supporting LNG carriers, cryogenic shipping operations, and next-generation clean energy maritime corridors.',
      gradient: 'from-teal-500 to-emerald-500',
    },
    {
      sector: 'Healthcare & Medical',
      icon: 'ri-hospital-line',
      description:
        'Medical-grade oxygen and nitrogen supply networks serving hospital systems, research laboratories, and pharmaceutical manufacturing facilities across multiple continents.',
      gradient: 'from-rose-500 to-pink-500',
    },
    {
      sector: 'Manufacturing & Industrial',
      icon: 'ri-factory-line',
      description:
        'Bulk cryogenic supply and pipeline distribution supporting steel fabrication, electronics manufacturing, food processing, and advanced materials production facilities.',
      gradient: 'from-amber-500 to-orange-500',
    },
  ];

  const metrics = [
    {
      value: '850K+',
      label: 'Tons Annual Production',
      icon: 'ri-gas-station-line',
    },
    {
      value: '4',
      label: 'Global Regions',
      icon: 'ri-global-line',
    },
    {
      value: '24/7',
      label: 'Supply Chain Operations',
      icon: 'ri-time-line',
    },
    {
      value: '99.9%',
      label: 'Delivery Reliability',
      icon: 'ri-shield-check-line',
    },
  ];

  return (
    <section
      id="partnerships"
      className="relative bg-gradient-to-b from-stone-200 to-stone-300 py-24 px-12 overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-10 h-1 bg-gradient-to-r from-transparent to-teal-500 rounded-full"></div>
            <span className="text-teal-600 text-sm font-bold tracking-wide uppercase">
              Strategic Partnerships
            </span>
            <div className="w-10 h-1 bg-gradient-to-r from-teal-500 to-transparent rounded-full"></div>
          </div>

          <h2 className="text-stone-800 font-black text-4xl mb-4">
            Powering{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-cyan-500">
              Global Industries
            </span>
          </h2>

          <p className="text-stone-600 text-base max-w-2xl mx-auto">
            SkyForge serves as a foundational supplier across aerospace, energy, healthcare, and
            manufacturing sectors — delivering infrastructure-scale production capacity and
            AI-powered supply chain intelligence worldwide.
          </p>
        </div>

        {/* Metrics Bar */}
        <div className="grid grid-cols-4 gap-6 mb-16">
          {metrics.map((metric, index) => (
            <div
              key={index}
              className="bg-stone-50 border border-stone-300 rounded-2xl p-6 text-center hover:shadow-xl transition-all shadow-lg"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className={`${metric.icon} text-xl text-white`}></i>
              </div>
              <div className="text-stone-800 font-black text-3xl mb-2">{metric.value}</div>
              <div className="text-stone-600 text-xs font-medium">{metric.label}</div>
            </div>
          ))}
        </div>

        {/* Partnership Sectors */}
        <div className="grid grid-cols-2 gap-6 mb-16">
          {partnerships.map((partnership, index) => (
            <div
              key={index}
              className="bg-stone-50 border border-stone-300 rounded-2xl p-8 hover:shadow-xl transition-all shadow-lg group"
            >
              <div className="flex items-start gap-5">
                <div
                  className={`w-14 h-14 bg-gradient-to-br ${partnership.gradient} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}
                >
                  <i className={`${partnership.icon} text-2xl text-white`}></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-stone-800 font-bold text-lg mb-3">{partnership.sector}</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {partnership.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Infrastructure Impact Statement */}
        <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-3xl p-10 text-center shadow-2xl">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-building-4-line text-3xl text-white"></i>
            </div>
            <h3 className="text-white font-black text-3xl mb-4">
              Infrastructure. Intelligence. Industrial Power.
            </h3>
            <p className="text-white/90 text-base leading-relaxed mb-6">
              From U.S. logistics corridors to international port systems, SkyForge combines Air
              Separation Unit deployment, cryogenic distribution networks, and AI-powered supply
              chain management to deliver foundational industrial gas infrastructure at global
              scale.
            </p>
            <div className="flex items-center justify-center gap-8">
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-white text-lg"></i>
                <span className="text-white text-sm font-medium">ASU Deployment</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-white text-lg"></i>
                <span className="text-white text-sm font-medium">Smart Logistics</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-white text-lg"></i>
                <span className="text-white text-sm font-medium">Global Network</span>
              </div>
            </div>
          </div>
        </div>

        {/* Certification Badges */}
        <div className="mt-14 flex items-center justify-center gap-8">
          <div className="flex items-center gap-2.5 bg-stone-50 border border-stone-300 rounded-full px-5 py-3 shadow-md">
            <i className="ri-shield-check-line text-lg text-teal-500"></i>
            <span className="text-stone-600 text-xs font-medium">ISO 9001 Certified</span>
          </div>
          <div className="flex items-center gap-2.5 bg-stone-50 border border-stone-300 rounded-full px-5 py-3 shadow-md">
            <i className="ri-award-line text-lg text-cyan-500"></i>
            <span className="text-stone-600 text-xs font-medium">ISO 17025 Accredited</span>
          </div>
          <div className="flex items-center gap-2.5 bg-stone-50 border border-stone-300 rounded-full px-5 py-3 shadow-md">
            <i className="ri-verified-badge-line text-lg text-teal-500"></i>
            <span className="text-stone-600 text-xs font-medium">FDA Registered</span>
          </div>
          <div className="flex items-center gap-2.5 bg-stone-50 border border-stone-300 rounded-full px-5 py-3 shadow-md">
            <i className="ri-medal-line text-lg text-cyan-500"></i>
            <span className="text-stone-600 text-xs font-medium">AS9100 Compliant</span>
          </div>
        </div>
      </div>
    </section>
  );
}
